<template>
  <div class="firebase-notice-log">
    <el-form :inline="true" class="filter-form" @submit.native.prevent="search">
      <el-form-item label="來源">
        <el-select v-model="filters.source" :disabled="loading" placeholder="請選擇來源">
          <el-option label="3gtapp" value="3gtapp" />
          <el-option label="tc" value="tc" />
          <el-option label="guideo" value="guideo" />
        </el-select>
      </el-form-item>
      <el-form-item label="Token">
        <el-input
          v-model.trim="filters.token"
          clearable
          :disabled="loading"
          placeholder="輸入完整 Token"
          style="width: 240px"
        />
      </el-form-item>
      <el-form-item label="紀錄時間">
        <el-date-picker
          v-model="filters.recordedAtRange"
          type="datetimerange"
          :disabled="loading"
          range-separator="至"
          start-placeholder="開始時間"
          end-placeholder="結束時間"
          format="yyyy-MM-dd HH:mm:ss"
          value-format="yyyy-MM-dd HH:mm:ss"
          clearable
        />
      </el-form-item>
      <el-form-item label="排序">
        <el-select v-model="filters.recordedAtOrder" :disabled="loading" style="width: 100px">
          <el-option label="新到舊" value="desc" />
          <el-option label="舊到新" value="asc" />
        </el-select>
      </el-form-item>
      <el-form-item label="每次載入">
        <el-select v-model="limit" :disabled="loading" style="width: 90px" @change="search">
          <el-option :value="10" label="10 筆" />
          <el-option :value="20" label="20 筆" />
          <el-option :value="50" label="50 筆" />
        </el-select>
      </el-form-item>
      <div style="margin: 8px 0 0 8px">
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search" circle>
        </el-button>
        <el-button icon="el-icon-refresh" :disabled="loading" @click="resetFilters" circle>
        </el-button>
      </div>
    </el-form>

    <div ref="listContainer" class="list-container" @scroll.passive="handleScroll">
      <el-table
        v-loading="loading && requests.length === 0"
        :data="requests"
        border
        fit
        highlight-current-row
        size="mini"
        style="width: 100%"
      >
        <el-table-column align="center" label="項次" width="60">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="紀錄時間" width="170">
          <template slot-scope="scope">
            {{ scope.row.recorded_at }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="Request ID" min-width="250" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.firebase_notification_request_id }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="來源 / 應用程式" min-width="200">
          <template slot-scope="scope">
            <div>{{ scope.row.source }}</div>
            <el-tag size="mini" type="info">{{ scope.row.application_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="left" label="通知標題" min-width="220">
          <template slot-scope="scope">
            {{ getNotification(scope.row).title || '-' }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="通知內容" min-width="280">
          <template slot-scope="scope">
            <span class="content-preview">{{ getNotification(scope.row).body || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column align="left" label="通知類型" width="160">
          <template slot-scope="scope">
            {{ getMessageData(scope.row).fcmType || '-' }}
          </template>
        </el-table-column>
        <el-table-column align="center" label="詳細內容" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showDetails(scope.row)"> 查看 </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="load-more">
        <span v-if="requests.length">{{ `已載入 ${requests.length} 筆` }}</span>
        <span v-if="!hasMore && requests.length">已無更多資料</span>
        <span v-else-if="!loading && !requests.length">查無資料</span>
        <el-button
          v-if="hasMore && requests.length"
          type="text"
          :loading="loading"
          @click="loadMore"
        >
          載入更多
        </el-button>
      </div>
    </div>

    <el-dialog
      title="Firebase 通知請求詳細資料"
      :visible.sync="detailVisible"
      width="760px"
      @closed="selectedRequest = null"
    >
      <div v-if="selectedRequest" class="request-details">
        <el-form label-width="120px" size="small">
          <el-form-item label="Request ID">
            <span>{{ selectedRequest.firebase_notification_request_id || '-' }}</span>
          </el-form-item>
          <el-form-item label="紀錄時間">
            <span>{{ selectedRequest.recorded_at || '-' }}</span>
          </el-form-item>
          <el-form-item label="來源">
            <span>{{ selectedRequest.source || '-' }}</span>
          </el-form-item>
          <el-form-item label="應用程式">
            <span>{{ selectedRequest.application_name || '-' }}</span>
          </el-form-item>
        </el-form>
        <el-alert title="通知請求中的 Token 已遮蔽。" type="info" :closable="false" show-icon />
        <pre class="payload-content">{{ formatPayload(selectedRequest.request_payload) }}</pre>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getFirebaseNotificationRequests } from '@/api/event'

export default {
  name: 'FirebaseNoticeLog',
  data() {
    return {
      filters: {
        source: '3gtapp',
        token: '',
        recordedAtRange: [],
        recordedAtOrder: 'desc',
      },
      limit: 20,
      offset: 0,
      requests: [],
      loading: false,
      hasMore: true,
      detailVisible: false,
      selectedRequest: null,
    }
  },
  created() {
    this.fetchRequests()
  },
  methods: {
    search() {
      if (this.loading) return

      this.requests = []
      this.offset = 0
      this.hasMore = true
      this.fetchRequests()
    },
    resetFilters() {
      this.filters = {
        source: '3gtapp',
        token: '',
        recordedAtRange: [],
        recordedAtOrder: 'desc',
      }
      this.limit = 20
      this.search()
    },
    async fetchRequests() {
      if (this.loading || !this.hasMore) return

      this.loading = true
      const params = {
        source: this.filters.source,
        recorded_at_order: this.filters.recordedAtOrder,
        limit: this.limit,
        offset: this.offset,
      }
      const token = this.filters.token.trim()
      if (token) params.token = token
      if (this.filters.recordedAtRange && this.filters.recordedAtRange.length === 2) {
        params.recorded_at_from = this.filters.recordedAtRange[0].replace(' ', 'T')
        params.recorded_at_to = this.filters.recordedAtRange[1].replace(' ', 'T')
      }

      try {
        const response = await getFirebaseNotificationRequests(params)
        if (!response || !response.success || !Array.isArray(response.data)) {
          this.$message.error((response && response.message) || '查詢 Firebase 通知請求失敗')
          return
        }

        this.requests = this.requests.concat(response.data)
        this.offset += response.data.length
        this.hasMore = response.data.length === this.limit
      } catch (error) {
        this.$message.error(error.message || '查詢 Firebase 通知請求失敗')
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.fetchRequests()
    },
    handleScroll() {
      const container = this.$refs.listContainer
      if (
        container &&
        container.scrollTop + container.clientHeight >= container.scrollHeight - 80
      ) {
        this.loadMore()
      }
    },
    getMessage(row) {
      return row && row.request_payload && row.request_payload.message
        ? row.request_payload.message
        : {}
    },
    getNotification(row) {
      const message = this.getMessage(row)
      return message.notification || {}
    },
    getMessageData(row) {
      const message = this.getMessage(row)
      return message.data || {}
    },
    showDetails(row) {
      this.selectedRequest = row
      this.detailVisible = true
    },
    formatPayload(payload) {
      return JSON.stringify(this.sanitizePayload(payload || {}), null, 2)
    },
    sanitizePayload(value, sensitive = false) {
      if (Array.isArray(value)) {
        return value.map(item => this.sanitizePayload(item, sensitive))
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).reduce((result, key) => {
          result[key] = this.sanitizePayload(value[key], sensitive || /token/i.test(key))
          return result
        }, Object.create(null))
      }
      if (sensitive && typeof value === 'string') {
        if (value.length <= 8) return '********'
        return `${value.slice(0, 4)}...${value.slice(-4)}`
      }
      return value
    },
  },
}
</script>

<style lang="scss" scoped>
.firebase-notice-log {
  padding: 15px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.list-container {
  height: calc(100vh - 230px);
  min-height: 300px;
  overflow: auto;
}

.content-preview {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.load-more {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  color: #909399;
}

.payload-content {
  max-height: 50vh;
  overflow: auto;
  padding: 12px;
  background: #f5f7fa;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
